import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { generalErrorAlert } from "../GeneralErrorAlert";

const DEBOUNCE_MS = 350;

/**
 * Debounced call to the payroll preview endpoint.
 *
 * The calculation itself lives on the server (it runs the official BMF Programmablaufplan),
 * so the browser never re-implements tax rules and both the preview and the saved salary slip
 * are guaranteed to agree.
 *
 * @param {string} [employeeId] Employee whose stored Lohnsteuerabzugsmerkmale are the defaults.
 * @returns {{payroll:object|null, loading:boolean, inputError:string|null, request:Function}}
 */
const usePayrollPreview = employeeId => {
  const [payroll, setPayroll] = useState(null);
  const [loading, setLoading] = useState(false);
  const [inputError, setInputError] = useState(null);
  const timerRef = useRef(null);
  // Responses can arrive out of order; only the newest request may update the view.
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(
    () => () => {
      mountedRef.current = false;
      clearTimeout(timerRef.current);
    },
    []
  );

  const fetchPreview = useCallback(
    async values => {
      const typedEnough =
        values?.brutto !== undefined && values?.brutto !== null && values?.steuerklasse;
      // With an employee the backend can fall back to their stored gross and Steuerklasse, so
      // it is worth asking even before anything is typed; without one we would only provoke a
      // 400 and show an error for a form the user has not filled in yet.
      if (!typedEnough && !employeeId) {
        setPayroll(null);
        setInputError(null);
        return null;
      }
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      setLoading(true);
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/salary-slips/preview`,
          { ...values, employee: employeeId },
          { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
        );
        if (!mountedRef.current || requestId !== requestIdRef.current) return null;
        setInputError(null);
        setPayroll(res.data.payroll);
        return res.data.payroll;
      } catch (err) {
        if (!mountedRef.current || requestId !== requestIdRef.current) return null;
        // A 400 means the entered values are wrong; that belongs next to the fields, not in
        // a global toast. Anything else is a real failure and goes through the usual alert.
        if (err.response?.status === 400) {
          setInputError(err.response.data?.errorMessage || "Invalid input");
          setPayroll(null);
        } else {
          generalErrorAlert(err);
        }
        return null;
      } finally {
        if (mountedRef.current && requestId === requestIdRef.current) setLoading(false);
      }
    },
    [employeeId]
  );

  /** Debounced entry point for "recalculate while the user types". */
  const request = useCallback(
    values => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => fetchPreview(values), DEBOUNCE_MS);
    },
    [fetchPreview]
  );

  return { payroll, loading, inputError, request, fetchPreview };
};

export default usePayrollPreview;
